import { useState, useCallback } from 'react';
import {
	searchSongMetadataOptionsWithRateLimit,
	fetchRecordingDetailsWithRateLimit,
} from '../../../../services/MusicService';
import { searchAppleMusicOptionsWithRateLimit } from '../../../../services/AppleMusicService';
import {
	searchDiscogsOptionsWithRateLimit,
	hydrateDiscogsOption,
} from '../../../../services/DiscogsService';
import { findRepertoireEntry } from '../../../../services/RepertoireService';
import { parseNaturalInput } from './songSearchParse';
import { resolveSongDisplayName } from './songDisplayName';
import { rankOptions } from './songSearchRank';

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Choose which MusicBrainz candidate an APPLE row should take its credits from.
 *
 * OBSERVED LIVE 2026-08-02, and it is why this function exists: picking the Apple
 * row "Tomorrow — Andrea McArdle — Annie (Original Broadway Cast Recording)"
 * returned composer **Marty Robbins**, who wrote a different song called
 * "Tomorrow". The graft simply took MusicBrainz's top hit for the bare title.
 *
 * This is NOT new — the previous implementation took the first candidate that
 * happened to carry credits, over the same arbitrarily-ranked list, and would
 * graft the same wrong name. But since search results already carry artist and
 * album, preferring a candidate that AGREES with the Apple row costs nothing.
 *
 * Deliberately a preference, not a filter: if nothing agrees we still fall back
 * to the top hit, because a plausible credit is more useful to a teacher than a
 * blank field. **That fallback is exactly the case that produced Marty Robbins,
 * so this narrows the defect rather than closing it** — a real match check
 * (title + artist + year, or refusing to graft at all when nothing agrees) is
 * its own decision and its own card.
 */
export const pickMatchingCandidate = (candidates = [], target = {}) => {
	const withId = candidates.filter((c) => c && c.musicbrainzId);
	if (!withId.length) return null;
	const artist = norm(target.artist);
	const album = norm(target.album);
	return (
		(artist && withId.find((c) => norm(c.artist) === artist)) ||
		(album && withId.find((c) => norm(c.album) === album)) ||
		(artist && withId.find((c) => norm(c.artist).includes(artist) || artist.includes(norm(c.artist)))) ||
		withId[0]
	);
};

/**
 * Add-a-song metadata search — extracted from SongNotes.js's handleAddSong /
 * enrichWithMusicbrainz / handleSelectMetadata flow.
 *
 * Search-first, never a gate (design handoff): the match fills the song header
 * (show, composer, year, key) and powers the lyrics fetch, but the manual path
 * is always one row away. The behavioral rules that carry over:
 *  - repertoire is checked first — an existing entry's metadata short-circuits
 *    the API fan-out;
 *  - the three sources fan out through safeApiCall so one failing API never
 *    sinks the search;
 *  - Apple results are enriched with MusicBrainz composer/lyricist credits.
 *
 * The hook only SEARCHES. Adding the chosen (name, metadata) to the board is
 * the caller's move (board.addSong) so this stays reusable from any sheet.
 */
const useSongMetadataSearch = ({ studentId = null }) => {
	const [isSearching, setIsSearching] = useState(false);
	const [options, setOptions] = useState([]);
	const [searchedQuery, setSearchedQuery] = useState(null); // { name, show, artist }
	const [searchError, setSearchError] = useState('');

	const reset = useCallback(() => {
		setOptions([]);
		setSearchedQuery(null);
		setSearchError('');
		setIsSearching(false);
	}, []);

	/**
	 * Apple results carry no writing credits; graft them from MusicBrainz.
	 *
	 * TWO calls now, and the second one is NOT optional. Under details-on-pick,
	 * MusicBrainz SEARCH results are credit-free by construction, so the old
	 * implementation — search, then `.find()` the first option carrying
	 * composers — would find nothing, every time, and silently return the Apple
	 * row unenriched. The credits live behind the detail lookup, so it has to
	 * search (1 request) and then hydrate the best candidate (1 request).
	 *
	 * Bounded to the single highest-scoring candidate on purpose. Hydrating all
	 * three to hunt for one with credits would re-create, in miniature, exactly
	 * the fan-out this change removed.
	 */
	const enrichWithMusicbrainz = useCallback(async (metadata, songName, artistName = '') => {
		if (!metadata || metadata.source !== 'apple') return metadata;
		try {
			// 12, not 3. `limit` is a query parameter on a SINGLE request, so a
			// wider candidate pool costs nothing and gives pickMatchingCandidate an
			// actual chance of finding the matching recording. Under the old
			// fan-out this number WAS the request count, which is why it was 3.
			const mbOptions = await searchSongMetadataOptionsWithRateLimit(songName, artistName, 12);
			const candidate = pickMatchingCandidate(mbOptions, metadata);
			if (!candidate) return metadata;
			const enriched = await fetchRecordingDetailsWithRateLimit(candidate.musicbrainzId);
			if (!enriched) return metadata;
			return {
				...metadata,
				composers: metadata.composers?.length ? metadata.composers : enriched.composers || [],
				lyricists: metadata.lyricists?.length ? metadata.lyricists : enriched.lyricists || [],
				arrangers: metadata.arrangers?.length ? metadata.arrangers : enriched.arrangers || [],
				musicalName: metadata.musicalName || enriched.musicalName || '',
				yearWritten: metadata.yearWritten || enriched.yearWritten || null,
				isMusical: metadata.isMusical || enriched.isMusical || false,
				workId: metadata.workId || enriched.workId || null,
				recordingId: metadata.recordingId || enriched.recordingId || metadata.recordingId,
				musicbrainzId: metadata.musicbrainzId || enriched.musicbrainzId || metadata.musicbrainzId,
			};
		} catch (err) {
			console.error('Error enriching Apple result with MusicBrainz:', err);
			return metadata;
		}
	}, []);

	/**
	 * Fill in what a LIST row deliberately does not carry.
	 *
	 * Search results are credit-free by construction (details-on-pick — see
	 * MusicService.buildOptionFromSearchResult): the fan-out that used to fetch
	 * composer/lyricist for all twelve rows cost ~35 requests and silently
	 * dropped a third of MusicBrainz to 503s. Now exactly ONE row is hydrated,
	 * and only once a teacher has chosen it.
	 *
	 * Routed by source, because each one needs a different call:
	 *   musicbrainz → the recording id is already in hand, so fetch it directly.
	 *                 Strictly better than the old path, which re-SEARCHED
	 *                 MusicBrainz even for rows that came FROM MusicBrainz.
	 *   discogs     → the release detail carries `extraartists` (the credits).
	 *                 Previously Discogs picks were never enriched at all,
	 *                 because enrichWithMusicbrainz returns early on any
	 *                 non-Apple source.
	 *   apple       → no id to look up, so the search-and-graft above stands.
	 *
	 * Every branch falls back to the untouched option: a failed enrichment must
	 * never cost the teacher the song they just picked.
	 */
	const hydrateOption = useCallback(
		async (metadata, songName, artistName = '') => {
			if (!metadata || metadata.detailsFetched) return metadata;

			if (metadata.source === 'musicbrainz' && metadata.musicbrainzId) {
				try {
					const detailed = await fetchRecordingDetailsWithRateLimit(metadata.musicbrainzId);
					return detailed ? { ...metadata, ...detailed, detailsFetched: true } : metadata;
				} catch (err) {
					console.warn('MusicBrainz detail fetch failed:', err.message);
					return metadata;
				}
			}

			if (metadata.source === 'discogs') {
				try {
					return await hydrateDiscogsOption(metadata);
				} catch (err) {
					console.warn('Discogs hydration failed:', err.message);
					return metadata;
				}
			}

			return enrichWithMusicbrainz(metadata, songName, artistName);
		},
		[enrichWithMusicbrainz]
	);

	/**
	 * Run the search for a raw input string ("tomorrow annie").
	 * Resolves to one of:
	 *   { kind: 'resolved', name, metadata }  — unambiguous (repertoire hit or
	 *                                           single API result); add directly.
	 *   { kind: 'options',  name }            — several candidates; render
	 *                                           `options` and let the teacher pick.
	 *   { kind: 'none',     name }            — nothing found; offer manual add.
	 */
	const search = useCallback(
		async (rawInput) => {
			const parsed = parseNaturalInput(rawInput);
			const songName = parsed.song || (rawInput || '').trim();
			if (!songName) return { kind: 'none', name: '' };

			const artistQuery = parsed.artist || '';
			const titleQuery = [songName, parsed.show].filter(Boolean).join(' ');

			setIsSearching(true);
			setSearchError('');
			setOptions([]);
			setSearchedQuery({ name: songName, show: parsed.show, artist: artistQuery });

			try {
				// Existing repertoire metadata wins — no API round-trip, and re-adding
				// a song re-attaches its history with the metadata it already has.
				if (studentId) {
					try {
						const existingEntry = await findRepertoireEntry(studentId, songName);
						if (existingEntry && existingEntry.metadata) {
							// The row's own stored name wins here: it is what the board and
							// the repertoire rail already show for this student, and an
							// existing entry may have been renamed by hand.
							return {
								kind: 'resolved',
								name: existingEntry.songName || resolveSongDisplayName(songName, existingEntry.metadata),
								metadata: existingEntry.metadata,
							};
						}
					} catch (repertoireError) {
						console.warn('Repertoire lookup failed, continuing with API search:', repertoireError.message);
					}
				}

				const safeApiCall = async (apiCall, sourceName) => {
					try {
						return await apiCall();
					} catch (err) {
						console.warn(`${sourceName} search failed:`, err.message);
						return [];
					}
				};

				const [appleOptions, discogsOptions, mbOptions] = await Promise.all([
					safeApiCall(() => searchAppleMusicOptionsWithRateLimit(titleQuery || songName, artistQuery, 12), 'Apple Music'),
					safeApiCall(() => searchDiscogsOptionsWithRateLimit(titleQuery || songName, artistQuery, 12), 'Discogs'),
					safeApiCall(() => searchSongMetadataOptionsWithRateLimit(titleQuery || songName, artistQuery, 12), 'MusicBrainz'),
				]);

				const found = [...discogsOptions, ...appleOptions, ...mbOptions];

				if (found.length === 1) {
					// An unambiguous hit is added WITHOUT the teacher picking, so it
					// has to be hydrated here too — otherwise the one path that skips
					// the result list would also skip its credits.
					const metadata = await hydrateOption(found[0], songName, artistQuery);
					return { kind: 'resolved', name: resolveSongDisplayName(songName, metadata), metadata };
				}
				if (found.length > 1) {
					// Cast recordings first, then soundtracks — the concat order above
					// (Discogs, Apple, MusicBrainz) is arbitrary with respect to which
					// recording a voice teacher actually wants. Ranked here rather than
					// at render time so `options` IS the display order and any consumer
					// of this hook gets it.
					setOptions(rankOptions(found));
					return { kind: 'options', name: songName };
				}
				return { kind: 'none', name: songName };
			} catch (error) {
				console.error('Error searching song metadata:', error);
				setSearchError('Search failed. You can still add the song manually.');
				return { kind: 'none', name: songName };
			} finally {
				setIsSearching(false);
			}
		},
		[studentId, hydrateOption]
	);

	/** The teacher picked one of `options` — hydrate it and hand it back. */
	const resolveOption = useCallback(
		async (metadata) => {
			// `hydrateOption` is still handed the TYPED text, because it matches
			// credits against what the teacher searched for; only the saved/
			// displayed name is resolved. See songDisplayName.js for why the
			// resolved title is adopted conditionally rather than always.
			const typed = searchedQuery?.name || '';
			const enriched = await hydrateOption(metadata, typed || metadata?.title || '', searchedQuery?.artist || '');
			return { name: resolveSongDisplayName(typed, enriched || metadata), metadata: enriched };
		},
		[searchedQuery, hydrateOption]
	);

	return { search, resolveOption, options, isSearching, searchedQuery, searchError, reset };
};

export default useSongMetadataSearch;
